<template>
  <section class="interview-section">
    <div class="interview-section__outer">
      <ImageCard
        class="interview-section__outer__img"
        v-bind="$tm(`${interviewPath}.cardContent`)"
        :cardType="$t('general.labels.interview')"
        :imagePath="imagePath"
        @onClick="showModal(true)"
      />

      <p
        class="interview-section__outer__card-text"
        v-html="$t(`${interviewPath}.cardSubscript`)"
      />
    </div>

    <div class="interview-section__inner">
      <TextQuote
        class="interview-section__inner__text"
        :paragraphs="paragraphs"
      />
      <ButtonPrimary
        :label="$t('general.ctas.readInterview')"
        @onClick="showModal(true)"
      />
    </div>

    <InterviewModal
      :show="modalShown"
      :cardSubscript="$t(`${interviewPath}.cardSubscript`)"
      :cardContent="$tm(`${interviewPath}.cardContent`)"
      :title="$tm(`${interviewPath}.title`)"
      :content="$tm(`${interviewPath}.content`)"
      :imagePath="imagePath"
      :internalUrlParam="currentPage"
      @onClose="showModal(false)"
      @onNavigate="navigateFromModal"
    />
  </section>
</template>

<script setup>
import { ref, computed } from "vue";
import { useI18n } from "vue-i18n";

import ButtonPrimary from "../general/ButtonPrimary.vue";
import ImageCard from "../general/ImageCard.vue";
import TextQuote from "../general/TextQuote.vue";
import InterviewModal from "../general/InterviewModal.vue";

const props = defineProps({
  id: String,
  currentPage: String,
  paragraphs: {
    type: Array,
    default: () => [],
  },
});

const { t } = useI18n();

const modalShown = ref(false);
const interviewIndex = computed(
  () => props.id.split("-")[props.id.split("-").length - 1]
);

const interviewPath = computed(() => {
  return `interviews.${props.currentPage}[${interviewIndex.value}]`;
});

const imagePath = computed(() => {
  return `${t("translation." + props.currentPage)}_interviste-${
    +interviewIndex.value + 1
  }`;
});

function showModal(value) {
  modalShown.value = value;
}

function navigateFromModal() {
  modalShown.value = false;

  // Wait until fade animation is done before scrolling
  setTimeout(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, 500);
}
</script>

<style lang="scss" scoped>
.interview-section {
  color: var(--white-100);
  padding: 0 var(--page-padding-h);
  background-image: var(--page-gradient);
  display: flex;
  position: relative;
  z-index: 1;

  .mobile &,
  .tablet & {
    flex-direction: column;
  }

  .mobile & {
    --max-card-width: 100%;
  }

  &__outer {
    display: flex;
    flex-direction: column;
    align-items: flex-end;

    .desktop &,
    .large & {
      transform: translateY(-2.5rem);
      width: var(--left-space);
      flex-shrink: 0;
    }

    &__img {
      .mobile &,
      .tablet & {
        margin-top: -2rem;
      }

      .tablet & {
        margin-left: toRem(48);
      }
    }

    &__card-text {
      max-width: var(--max-card-width);
      padding-top: 1.5rem;
      padding-right: 2rem;

      .mobile &,
      .tablet & {
        @include p-s;

        padding: var(--page-padding-h);
        border-left: 1px solid var(--white-100);
        border-right: 1px solid var(--white-100);
      }

      .tablet & {
        max-width: 100%;
        border: none;
      }
    }

    .tablet & {
      align-items: flex-start;
      border-right: 1px solid var(--white-100);
      border-left: 1px solid var(--white-100);
    }
  }

  &__inner {
    border-left: 1px solid var(--white-100);
    border-right: 1px solid var(--white-100);
    position: relative;

    .desktop &,
    .large & {
      padding: toRem(100) toRem(53);
    }

    .mobile &,
    .tablet & {
      padding: var(--page-padding-h);
      padding-bottom: 2rem;

      &::before {
        content: "";
        position: absolute;
        top: 0;
        left: var(--page-padding-h);
        background-color: var(--white-40);
        width: calc(100% - var(--page-padding-h) * 2);
        height: 1px;
      }
    }

    &__text {
      margin-bottom: 1.75rem;
    }
  }
}
</style>
